import { useCallback, useMemo } from "react";
import {
  useClient,
  useProposal,
  useProposalReportData,
  useProposals,
} from "creso-core/hooks";
import { ReportPreview } from "@/modules/reports/components/report-preview";
import { getPdfDownloadUrl } from "@/utils/common";
import { V2Loader } from "web-design-system";
import { useDownloadProposal } from "@planning/hooks";
import { useProposalBuilderContext } from "@planning/providers/proposal-provider";
import type { ReportPreviewSection } from "@/modules/reports/components/report-preview";

interface ProposalPreviewDialogProps {
  proposalId: number;
  clientId: number;
}

export function ProposalPreview({
  proposalId,
  clientId,
}: ProposalPreviewDialogProps) {
  const { prev, backtestPeriod, backtestBenchmark } =
    useProposalBuilderContext();

  const {
    data: proposal,
    isLoading: isProposalLoading,
    refetch,
  } = useProposal({ proposalId }, { enabled: !!proposalId });
  const { data: client, isLoading: isClientLoading } = useClient(
    clientId,
    !!clientId
  );
  const { data: reportData, isLoading: isReportLoading } =
    useProposalReportData({ proposalId }, { enabled: !!proposalId });

  const downloadProposal = useDownloadProposal(proposalId as number);

  const { refetch: fetchProposals } = useProposals(
    { clientId },
    { enabled: false }
  );

  const download = useCallback(
    async (url: string) => {
      const success = await downloadProposal(url);
      if (!success) return success;
      if (proposal?.pdf_status === "NONE") {
        fetchProposals();
      }
      refetch();
      return success;
    },
    [downloadProposal, proposal, fetchProposals, refetch]
  );

  const sections = useMemo(() => {
    if (!proposal) return [];

    const isGoalLinked = Boolean(proposal.goal);
    const hasSip = Number(proposal.total_sip_amount) > 0;
    const hasLumpsum = Number(proposal.total_lumpsum_amount) > 0;

    const hasSipAssetAllocation =
      hasSip &&
      (reportData?.sip_allocation_breakdown?.buckets ?? []).length > 0;
    const hasSipSectors =
      hasSip && (reportData?.sip_top_sectors ?? []).length > 0;
    const hasSipStocks =
      hasSip && (reportData?.sip_top_stocks ?? []).length > 0;

    const hasLumpsumAssetAllocation =
      hasLumpsum &&
      (reportData?.lumpsum_allocation_breakdown?.buckets ?? []).length > 0;
    const hasLumpsumSectors =
      hasLumpsum && (reportData?.lumpsum_top_sectors ?? []).length > 0;
    const hasLumpsumStocks =
      hasLumpsum && (reportData?.lumpsum_top_stocks ?? []).length > 0;

    const out: ReportPreviewSection[] = [{ id: "cover", label: "Cover Page" }];

    if (isGoalLinked || !!proposal.projected_value) {
      out.push({ id: "goal-overview", label: "Goal Overview" });
      if (hasSip) {
        out.push({ id: "sip-journey", label: "SIP Investment Journey" });
      }
    }

    if (hasSip) {
      out.push({ id: "sip-recommendations", label: "SIP Recommendation" });
    }

    if (hasLumpsum) {
      out.push({
        id: "lumpsum-recommendations",
        label: "One-time Recommendation",
      });
    }

    const sipBreakdownChildren: ReportPreviewSection[] = [];
    if (hasSipAssetAllocation)
      sipBreakdownChildren.push({
        id: "sip-asset-allocation",
        label: "Asset Allocation",
      });
    if (hasSipSectors)
      sipBreakdownChildren.push({
        id: "sip-sector-exposure",
        label: "Sector Exposure",
      });
    if (hasSipStocks)
      sipBreakdownChildren.push({
        id: "sip-stock-exposure",
        label: "Stock Exposure",
      });

    if (sipBreakdownChildren.length > 0) {
      out.push({
        id: "sip-breakdown",
        label: "SIP Breakdown",
        children: sipBreakdownChildren,
      });
    }

    const lumpsumBreakdownChildren: ReportPreviewSection[] = [];
    if (hasLumpsumAssetAllocation)
      lumpsumBreakdownChildren.push({
        id: "lumpsum-asset-allocation",
        label: "Asset Allocation",
      });
    if (hasLumpsumSectors)
      lumpsumBreakdownChildren.push({
        id: "lumpsum-sector-exposure",
        label: "Sector Exposure",
      });
    if (hasLumpsumStocks)
      lumpsumBreakdownChildren.push({
        id: "lumpsum-stock-exposure",
        label: "Stock Exposure",
      });

    if (lumpsumBreakdownChildren.length > 0) {
      out.push({
        id: "lumpsum-breakdown",
        label: "One-time Breakdown",
        children: lumpsumBreakdownChildren,
      });
    }

    if (hasSip || hasLumpsum) {
      out.push({ id: "backtest", label: "Returns Backtest" });
    }

    return out;
  }, [proposal, reportData]);

  const reportUrl = useMemo(() => {
    const base = getPdfDownloadUrl(
      `/reports/clients/${client?.id}/proposal/${proposalId}`
    );
    const url = new URL(base);
    url.searchParams.set("backtest_period", backtestPeriod);
    url.searchParams.set("backtest_benchmark", backtestBenchmark);
    return url.toString();
  }, [client?.id, proposalId, backtestBenchmark, backtestPeriod]);

  if (!proposalId || !clientId) return null;

  return (
    <V2Loader
      className="size-full"
      isLoading={isClientLoading || isProposalLoading || isReportLoading}
    >
      <ReportPreview
        reportUrl={reportUrl}
        back={prev}
        sections={sections}
        title="Proposal Report"
        downloadFileName={`${proposal?.client_name}-${proposal?.name}.pdf`}
        download={download}
      />
    </V2Loader>
  );
}
